import React from 'react';
import { motion } from 'framer-motion';
import { ArrowRight, Sparkles } from 'lucide-react';
import { openCalendly } from '../lib/calendly';
import WorldMapBackground from './WorldMapBackground';

const Hero: React.FC = () => {
  const coverageAreas = ['Kubernetes', 'CI/CD', 'Observability', 'Security'];

  return (
    <section className="relative h-screen flex items-center justify-center overflow-hidden bg-dark-950">
      {/* Global infrastructure background */}
      <div className="hero-background absolute inset-0">
        <WorldMapBackground />
      </div>

      {/* Overlay Gradient */}
      <div className="absolute inset-0 bg-gradient-to-b from-dark-950/40 via-dark-900/60 to-dark-950"></div>

      {/* Content */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center relative z-10 pt-32">

        {/* Eyebrow */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, ease: [0.16, 1, 0.3, 1] }}
          className="inline-flex items-center space-x-2 glass-premium rounded-full px-5 py-2 mb-8"
        >
          <Sparkles className="w-4 h-4 text-neon-blue" />
          <span className="text-sm font-medium text-gray-300">AI-Powered Cloud &amp; DevOps Platform</span>
        </motion.div>

        {/* Main Headline */}
        <motion.h1
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 1, delay: 0.2, ease: [0.16, 1, 0.3, 1] }}
          className="text-5xl md:text-7xl lg:text-8xl font-semibold mb-8 leading-tight"
        >
          <span className="text-white">Operate your infrastructure</span>
          <br />
          <span className="gradient-text">with an AI-native control plane</span>
        </motion.h1>

        {/* Subtitle */}
        <motion.p
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 1, delay: 0.4, ease: [0.16, 1, 0.3, 1] }}
          className="text-xl md:text-2xl text-gray-300 mb-12 max-w-4xl mx-auto leading-relaxed"
        >
          DeploAI is building a single platform to deploy applications, operate{' '}
          <span className="gradient-text-subtle">Kubernetes</span>, monitor infrastructure, and
          investigate production issues with AI assistance — instead of switching between a
          dozen separate tools.
        </motion.p>

        {/* CTA Button */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 1, delay: 0.6, ease: [0.16, 1, 0.3, 1] }}
          className="flex items-center justify-center mb-16"
        >
          <button
            onClick={() => openCalendly('trial')}
            className="premium-button text-lg px-8 py-4 flex items-center space-x-2 group"
          >
            <span>Get Started</span>
            <ArrowRight className="w-5 h-5 group-hover:translate-x-1 transition-transform duration-200" />
          </button>
        </motion.div>

        {/* Coverage areas */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 1, delay: 0.8, ease: [0.16, 1, 0.3, 1] }}
          className="flex flex-wrap justify-center items-center gap-3"
        >
          {coverageAreas.map((area) => (
            <div key={area} className="glass-premium rounded-full px-5 py-2">
              <span className="text-sm font-medium text-gray-300">{area}</span>
            </div>
          ))}
        </motion.div>
      </div>

      {/* Scroll Indicator */}
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ duration: 1, delay: 2 }}
        className="absolute bottom-8 left-1/2 transform -translate-x-1/2"
      >
        <div className="flex flex-col items-center space-y-2">
          <span className="text-sm text-gray-400">Scroll to explore</span>
          <div className="w-6 h-10 border-2 border-gray-400 rounded-full flex justify-center">
            <motion.div
              animate={{ y: [0, 12, 0] }}
              transition={{ duration: 2, repeat: Infinity }}
              className="w-1 h-3 bg-gray-400 rounded-full mt-2"
            />
          </div>
        </div>
      </motion.div>
    </section>
  );
};

export default Hero; 